import { FLAGS_WITH_SEPARATORS } from "../../../actions/Detail/constants";
import { DEFAULT_FACEBOOK_PAGE_IDS } from "../../../actions/App/constants";
import { generateMeta } from "../../../actions/App/utils";
import { stripBetaPrefix, makeAbsoluteCBCDomain } from "../../path/library";
import { getAuthorBioImage, createMetadataImage } from "./media";
import { getVfUrl } from "../../viafoura/library";
import { createAuthorbioHeadline } from "./content";

export const isValidContentId = (id) => {
    if (typeof id === "string") {
        if (id.match(/^(1|9)\.[0-9]+$/)) {
            return true;
        }
    }
    return false;
};

/**
 * @param {object[]} subject
 * @returns {array}
 */
export function concatSubjects(subject) {
    if (!subject) {
        return undefined;
    }
    return getSubjectNames(subject).concat(getSubjectPrefix(subject));
}

/**
 * @param {object[]} subject
 * @param {string} subject[].name
 * @returns {array}
 */
function getSubjectPrefix(subject) {
    if (!subject) {
        return undefined;
    }
    return Array.from(
        subject.reduce((red, obj) => {
            const name = obj?.name || "";
            return red.add(name.split("/")[0].replace(/[^a-zA-Z0-9-_]/g, "-"));
        }, new Set())
    );
}

/**
 * @param {object[]} subject
 * @param {string} subject[].name
 * @returns {array}
 */
function getSubjectNames(subject) {
    if (!subject) {
        return undefined;
    }
    return subject.map((obj) => {
        const name = obj.name;
        return name ? name.replace(/[^a-zA-Z0-9-_]/g, "-") : name;
    });
}

export const getAppUpdate = (json) => {
    const departmentLabel =
        json.departments && json.departments.length
            ? json.departments[0].name
            : "";

    const hasFlag = (flag) => {
        if (flag && FLAGS_WITH_SEPARATORS.includes(flag)) {
            return `${flag} | `;
        }
        return "";
    };

    const isSponsored = json.type === "sponsoredcontent";

    const sponsorName = isSponsored
        ? json?.sponsor?.image?.title?.toLowerCase?.()?.search?.("logo") > -1
            ? json?.sponsor?.image?.title?.slice?.(0, -5)
            : json?.sponsor?.image?.title
        : "";

    const title =
        json.headline || createAuthorbioHeadline(json) || json.title || "";
    const pageTitle = isSponsored
        ? `Sponsor Content with ${sponsorName}: ${title} | CBC`
        : `${title} | CBC ${departmentLabel}`;
    const sharedFlag = json.flag ? hasFlag(json.flag.toUpperCase()) : "";
    const shareTitle = isSponsored
        ? pageTitle
        : `${sharedFlag}${
              json.shareHeadline || title
          } | CBC ${departmentLabel}`;

    //jsonld content
    const jsonld = json.schema;

    // Photogallery has a unique ID field
    const viafouraId = json.id;

    const viafouraSect = json.vfsection || "";

    // Polopoly Json sometimes will output relative URL path eg. Video
    // Hence need to secure both OG:URL and Canonical URL to be absolute path
    const url = stripBetaPrefix(makeAbsoluteCBCDomain(json.url));

    const headlineImage = getAuthorBioImage(json) || createMetadataImage(json);

    // Polopoly Story content || Media || Photogallery description || author biography
    const description = json.description || json.biography || "";

    // The IDs of the facebook pages that can edit the details of this content when posting. (Facebook Link Ownership)
    const facebookPageIds = json.fbpages || DEFAULT_FACEBOOK_PAGE_IDS;

    const vfUrl = getVfUrl(url);
    const meta = generateMeta({
        "vf:container_id": viafouraId,
        "vf:section": viafouraSect,
        "vf:url": vfUrl,
        // TEMP; strip beta prefix (cf. definition of stripBetaPrefix() for more info)
        "og:url": url,
        "og:title": shareTitle,
        "og:image": headlineImage,
        "og:description": description,
        description: description,
        "og:type": "article",
        "twitter:card": "summary_large_image",
        "twitter:site": "@cbc",
        "twitter:title": shareTitle,
        "twitter:image": headlineImage,
        "twitter:description": description,
        "fb:pages": facebookPageIds,
        // TEMP; strip beta prefix (cf. definition of stripBetaPrefix() for more info)
        canonical: url,
        jsonld: jsonld,
    });

    return { pageTitle, meta };
};

export const getIsLiveStream = (streamType) => streamType === "Live";

export const isMpxId = (id) => /^\d+$/.test(id);

export const isSourceId = (id) => /^\d\.\d+$/.test(id);
